import React, { useState } from 'react';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Box,
  AppBar,
  Toolbar,
  Button,
  TextField,
  MenuItem,
  Chip,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import SubscriptionDetails from '../components/SubscriptionDetails';
import { isInvoiceNotification, isSubscriptionNotification } from '../utils/callbacks';

interface RustoreCallback {
  id: number;
  app_id: string;
  notification_type: string;
  product_code: string;
  change_status_time: string;
  status_new: string;
  status_old: string;
  purchase_token: string;
  invoice_id: string;
  order_id: string;
  purchase_id: string;
  developer_payload?: string;
  test_data?: string;
  subscription_id?: string | null;
  subscription_event?: string | null;
  next_charge_time?: string | null;
  auto_renewing?: boolean | null;
  subscription_raw?: Record<string, unknown> | null;
  is_valid: boolean;
  created_at: string;
  is_sandbox: boolean;
}

interface CallbacksResponse {
  data: RustoreCallback[];
  meta: {
    total: number;
    page: number;
    pageSize: number;
  };
}

interface AppIDsResponse {
  data: string[];
}

const notificationTypeOptions = [
  { label: 'All types', value: '' },
  { label: 'Invoice status', value: 'INVOICE_STATUS' },
  { label: 'Invoice status (sandbox)', value: 'INVOICE_STATUS_SANDBOX' },
  { label: 'Subscription event', value: 'SUBSCRIPTION_EVENT' },
  { label: 'Subscription event (sandbox)', value: 'SUBSCRIPTION_EVENT_SANDBOX' },
  { label: 'Test event', value: 'TEST_EVENT' },
];

const environmentOptions = [
  { label: 'Any environment', value: '' },
  { label: 'Production', value: 'production' },
  { label: 'Sandbox', value: 'sandbox' },
];

const getStatusColor = (status: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
  switch (status) {
    case 'paid':
      return 'info';
    case 'confirmed':
      return 'success';
    case 'cancelled':
      return 'error';
    case 'reversed':
      return 'warning';
    case 'refunded':
      return 'secondary';
    case 'executed':
      return 'primary';
    default:
      return 'default';
  }
};

const Callbacks: React.FC = () => {
  const { token, username, logout } = useAuth();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedApp, setSelectedApp] = useState<string>("");
  const [notificationType, setNotificationType] = useState<string>("");
  const [environmentFilter, setEnvironmentFilter] = useState<string>("");
  const [subscriptionIdFilter, setSubscriptionIdFilter] = useState<string>("");

  // Fetch unique app IDs
  const { data: appIDsData } = useQuery<AppIDsResponse>(
    ['appIDs'],
    async () => {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/callbacks/rustore/app-ids`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    }
  );

  const { data, isLoading, error } = useQuery<CallbacksResponse>(
    ['callbacks', page, rowsPerPage, selectedApp, notificationType, environmentFilter, subscriptionIdFilter],
    async () => {
      const url = selectedApp 
        ? `${process.env.REACT_APP_API_URL}/api/callbacks/rustore/${selectedApp}`
        : `${process.env.REACT_APP_API_URL}/api/callbacks/rustore`;

      const params: Record<string, string | number> = {
        page: page + 1,
        pageSize: rowsPerPage,
      };

      if (notificationType) {
        params.notification_type = notificationType;
      }

      if (subscriptionIdFilter) {
        params.subscription_id = subscriptionIdFilter;
      }

      if (environmentFilter) {
        params.environment = environmentFilter;
      }

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params,
      });
      return response.data;
    }
  );

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleNotificationTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNotificationType(event.target.value);
    setPage(0);
  };

  const handleEnvironmentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEnvironmentFilter(event.target.value);
    setPage(0);
  };

  const handleSubscriptionFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSubscriptionIdFilter(event.target.value);
    setPage(0);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (isLoading) {
    return (
      <Container>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Typography color="error">Error loading callbacks</Typography>
      </Container>
    );
  }

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            RuStore API Server
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography>
              User: {username}
            </Typography>
            <Button color="inherit" onClick={handleLogout}>
              Logout
            </Button>
          </Box>
        </Toolbar>
      </AppBar>
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            RuStore Callbacks
          </Typography>
          <TextField
            select
            label="Filter by App"
            value={selectedApp}
            onChange={(e) => setSelectedApp(e.target.value)}
            sx={{ minWidth: 200, mb: 2 }}
          >
            <MenuItem value="">All Apps</MenuItem>
            {appIDsData?.data.map((appId) => (
              <MenuItem key={appId} value={appId}>{appId}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Notification Type"
            value={notificationType}
            onChange={handleNotificationTypeChange}
            sx={{ minWidth: 240, mb: 2, ml: { xs: 0, sm: 2 } }}
          >
            {notificationTypeOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Environment"
            value={environmentFilter}
            onChange={handleEnvironmentChange}
            sx={{ minWidth: 200, mb: 2, ml: { xs: 0, sm: 2 } }}
          >
            {environmentOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Subscription ID"
            value={subscriptionIdFilter}
            onChange={handleSubscriptionFilterChange}
            sx={{ minWidth: 240, mb: 2, ml: { xs: 0, sm: 2 } }}
            placeholder="sub-123..."
          />
        </Box>
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>App ID</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Subscription</TableCell>
                  <TableCell>Product Code</TableCell>
                  <TableCell>Status Change</TableCell>
                  <TableCell>Invoice ID</TableCell>
                  <TableCell>Order ID</TableCell>
                  <TableCell>Purchase ID</TableCell>
                  <TableCell>Valid</TableCell>
                  <TableCell>Environment</TableCell>
                  <TableCell>Created At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.data.map((callback) => (
                  <TableRow key={callback.id}>
                    <TableCell>{callback.id}</TableCell>
                    <TableCell>{callback.app_id}</TableCell>
                    <TableCell>
                      <Chip 
                        label={callback.notification_type} 
                        size="small" 
                        color={
                          callback.notification_type === "TEST_EVENT"
                            ? "secondary"
                            : isSubscriptionNotification(callback.notification_type)
                              ? "success"
                              : "primary"
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {isSubscriptionNotification(callback.notification_type) ? (
                        <SubscriptionDetails
                          subscriptionId={callback.subscription_id}
                          subscriptionEvent={callback.subscription_event}
                          nextChargeTime={callback.next_charge_time}
                          autoRenewing={callback.auto_renewing}
                        />
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {callback.notification_type === "TEST_EVENT" ? (
                        <Typography variant="body2" color="textSecondary">
                          {callback.test_data}
                        </Typography>
                      ) : (
                        callback.product_code
                      )}
                    </TableCell>
                    <TableCell>
                      {isInvoiceNotification(callback.notification_type) && (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Chip 
                            label={callback.status_old} 
                            size="small" 
                            color={getStatusColor(callback.status_old)}
                          />
                          →
                          <Chip 
                            label={callback.status_new} 
                            size="small" 
                            color={getStatusColor(callback.status_new)}
                          />
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>{callback.invoice_id}</TableCell>
                    <TableCell>{callback.order_id}</TableCell>
                    <TableCell>{callback.purchase_id}</TableCell>
                    <TableCell>
                      <Chip 
                        label={callback.is_valid ? "Valid" : "Invalid"} 
                        color={callback.is_valid ? "success" : "error"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={callback.is_sandbox ? "Sandbox" : "Production"} 
                        color={callback.is_sandbox ? "warning" : "info"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(callback.created_at).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={data?.meta.total || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Container>

    </>
  );
};

export default Callbacks; 
